<template>
  <v-select
    v-model="selected"
    :options="computedOptions"
    :filterable="filterable"
    :placeholder="placeholder"
    :selectable="item => !item.disabled && !disabled"
    :disabled="disabled"
    :reduce="reduce"
    :loading="computedLoading"
    :label="optionLabel"
    :clearable="clearable"
    :append-to-body="appendToBody"
    :calculate-position="calculatePosition"
    class="ui-select"
    @open="onOpen"
    @close="onClose"
    @search="onSearchInput"
    @option:selected="onOptionSelected"
  >
    <template #option="option">
      <slot
        name="optionValue"
        :value="option"
      >
        {{ option[optionLabel] || option }}
      </slot>
    </template>
    <template #no-options="{ search, searching }">
      <slot
        name="no-options"
        :search="search"
        :searching="searching"
      >
        <template v-if="isFetchingSearch && searching">
          <em>Mencari '{{ search }}'...</em>
        </template>
        <template v-else-if="searching">
          <em>'{{ search }}' tidak ditemukan</em>
        </template>
        <em v-else>Tidak ada data</em>
      </slot>
    </template>
    <template #list-footer>
      <!-- Infinite scroll sentinel & loading indicator -->
      <li
        v-show="computedHasNextPage"
        ref="load"
        class="text-center flex justify-center items-center gap-[8px] h-[40px] text-[13px] text-[#737373]"
      >
        <template v-if="computedIsFetchingMore">
          <b-spinner
            small
            variant="primary"
          />
          <span>Memuat data...</span>
        </template>
      </li>
      <!-- Non-blocking backend search indicator; shown below existing results -->
      <li
        v-if="isFetchingSearch"
        class="search-loading-li"
      >
        <b-spinner
          small
          variant="secondary"
        />
        <span>Mencari...</span>
      </li>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select'
import { BSpinner } from 'bootstrap-vue'
import _ from 'lodash'

export default {
  name: 'Select',
  components: {
    vSelect,
    BSpinner,
  },
  props: {
    value: {
      type: [Object, String, Number, null],
      default: null,
    },
    defaultValue: {
      type: [Object, String, Number, null],
      default: null,
    },
    fetcher: {
      type: Object,
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionLabel: {
      type: String,
      default: 'name',
    },
    placeholder: {
      type: String,
      default: 'Pilih opsi',
    },
    hasNextPage: {
      type: Boolean,
      default: false,
    },
    debounceTimeout: {
      type: Number,
      default: 300,
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    calculatePosition: {
      type: Function,
      default: () => {},
    },
  },
  data: () => ({
    observer: null,
    debouncedSearchFn: null,
    lastScrollPosition: 0,
    isOnSearch: false,
    /**
     * Full option object of the currently selected item.
     * Anchors the item in computedOptions so vue-select can always render
     * its label even when fetcher.data resets mid-search (prevents flicker-to-ID).
     */
    selectedItem: null,
    /**
     * True while a backend search fetch is in-flight.
     * Used to show the inline footer spinner and suppress the global overlay.
     */
    isFetchingSearch: false,
    /**
     * Reactive list that drives computedOptions when a fetcher is provided.
     * Items are only ever added (merge-only), never wholesale replaced,
     * so the dropdown never goes blank during a search round-trip.
     */
    localOptions: [],
    /**
     * Snapshot of localOptions taken the moment a new backend search fires.
     * Displayed while the request is in-flight so users see the last known
     * results instead of an empty list.
     */
    pendingOptions: null,
  }),
  computed: {
    selected: {
      get() {
        return this.normalizeSelection(this.value ?? _.cloneDeep(this.defaultValue) ?? null)
      },
      set(newValue) {
        // Clear the selectedItem anchor when the user clears the selection.
        if (!newValue) this.selectedItem = null
        this.$emit('input', this.normalizeSelection(newValue))
      },
    },
    computedOptions() {
      // No fetcher → use plain options prop unchanged.
      if (!this.fetcher) return this.options

      // While a backend search is in-flight show the snapshot so the list
      // stays populated. Once the fetch resolves we switch to localOptions.
      const base = this.isFetchingSearch
        ? (this.pendingOptions || this.localOptions)
        : this.localOptions

      // Always inject the selected item at the top when it's absent from
      // base. This keeps vue-select able to render the label rather than
      // the raw reduced value (ID), which is the root cause of the flicker.
      if (this.selectedItem) {
        const selId = this.selectedItem.id
        if (!base.some(o => o.id === selId)) {
          return [this.selectedItem, ...base]
        }
      }
      return base
    },
    computedHasNextPage() {
      if (this.fetcher && typeof this.fetcher.hasMore === 'boolean') {
        return this.fetcher.hasMore
      }
      return this.hasNextPage
    },
    computedLoading() {
      if (this.fetcher && typeof this.fetcher.isLoading === 'boolean') {
        // Show top input spinner ONLY on initial load (when list is still empty)
        return this.fetcher.isLoading && this.localOptions.length === 0 && !this.isFetchingSearch
      }
      return this.loading && (!this.options || this.options.length === 0)
    },
    computedIsFetchingMore() {
      if (this.fetcher && typeof this.fetcher.isLoading === 'boolean') {
        return this.fetcher.isLoading && !this.isFetchingSearch && this.localOptions.length > 0
      }
      return this.loading && Boolean(this.options && this.options.length > 0)
    },
  },
  watch: {
    computedOptions: {
      handler() {
        if (!this.observer || !this.$refs.load) return
        this.$nextTick(() => {
          if (this.observer && this.$refs.load) {
            this.observer.observe(this.$refs.load)
          }
        })
      },
      deep: false,
    },
    /**
     * Syncs fetcher.data into localOptions each time a fetch completes
     * (isLoading: true → false).
     *
     * Search fetch  → replace localOptions with fresh server results.
     * Scroll fetch  → merge only items whose id is new (O(1) via idsSet).
     *
     * Using isLoading as the sync trigger (rather than watching fetcher.data
     * directly) means we only process a stable, complete result set and never
     * react to the transient empty-array state that some fetchers emit on reset.
     */
    'fetcher.isLoading': {
      handler(newVal, oldVal) {
        // Only react to the true → false transition (fetch just finished).
        if (newVal || !oldVal) return

        const newData = this.fetcher?.data || []

        if (this.isFetchingSearch) {
          this.isFetchingSearch = false
          this.pendingOptions = null
        }

        // Merge only items whose id is not already tracked by idsSet (O(1) per item).
        // Existing local items are always preserved and never deleted.
        const toAdd = newData.filter(item => {
          if (!this.idsSet.has(item.id)) {
            this.idsSet.add(item.id)
            return true
          }
          return false
        })

        if (toAdd.length > 0) {
          this.localOptions = [...this.localOptions, ...toAdd]
        }
      },
    },
  },
  created() {
    // Non-reactive Set for O(1) ID deduplication.
    // Kept outside data() because Vue 2 cannot track Map/Set mutations;
    // localOptions (the array) is the reactive source of truth.
    this.idsSet = new Set()

    this.debouncedSearchFn = _.debounce(query => {
      this.isOnSearch = true
      if (this.fetcher && typeof this.fetcher.fetchData === 'function') {
        // Snapshot current list before the fetch resets fetcher.data so the
        // dropdown keeps showing local results while the request is in-flight.
        this.pendingOptions = [...this.localOptions]
        this.isFetchingSearch = true
        this.fetcher.fetchData({
          reset: true,
          options: {
            ...this.fetcher.options,
            search: query,
          },
        })
      }
      this.$emit('on-search', query)
    }, this.debounceTimeout)
  },
  mounted() {
    this.observer = new IntersectionObserver(this.infiniteScroll)

    // Seed localOptions if the fetcher already has data when the component
    // mounts (e.g. parent pre-fetched before this component was created).
    if (this.fetcher && Array.isArray(this.fetcher.data) && this.fetcher.data.length > 0) {
      this.localOptions = this.fetcher.data.filter(item => {
        if (!this.idsSet.has(item.id)) {
          this.idsSet.add(item.id)
          return true
        }
        return false
      })
    }
  },
  updated() {
    if (!this.isOnSearch) {
      const dropdown = this.$el ? this.$el.querySelector('ul') : null
      if (dropdown) {
        dropdown.scrollTop = this.lastScrollPosition
      }
    }
  },
  methods: {
    normalizeSelection(val) {
      if (_.isObject(val)) {
        return ((_.get(val, this.optionLabel) ?? '') !== '') ? val : null
      }
      if (_.isString(val)) {
        return val.trim() !== '' ? val : null
      }
      return val ?? null
    },
    onSearchInput(query) {
      if (this.debouncedSearchFn) {
        this.debouncedSearchFn(query)
      }
    },
    /**
     * Capture the full option object on selection.
     * computedOptions uses this to keep the item in the list even when
     * fetcher.data resets, preventing the flicker-to-ID bug.
     */
    onOptionSelected(option) {
      this.selectedItem = option
    },
    async onOpen() {
      await this.$nextTick()
      if (this.observer && this.$refs.load) {
        this.observer.observe(this.$refs.load)
      }
      this.$emit('open')
    },
    onClose() {
      if (this.observer) {
        this.observer.disconnect()
      }
      this.$emit('on-close')
      this.$emit('close')
    },
    async infiniteScroll([{ isIntersecting, target }]) {
      const isAlreadyLoading = Boolean(this.fetcher?.isLoading || this.computedIsFetchingMore)
      if (isIntersecting && this.computedHasNextPage && !isAlreadyLoading) {
        this.observer.unobserve(target)
        if (this.isOnSearch) this.isOnSearch = false
        const ul = target.offsetParent
        if (!ul) {
          this.observer.disconnect()
          return
        }
        if (this.fetcher && typeof this.fetcher.fetchData === 'function') {
          this.fetcher.fetchData()
        }
        this.$emit('on-intersect')
        this.lastScrollPosition = ul.scrollTop + 50
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.ui-select {
  font-size: 14px;

  .vs__dropdown-toggle {
    min-height: 36px !important;
    height: 36px !important;
    border-radius: 8px !important;
    border-color: #E5E5E5 !important;
    padding: 0 10px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
  }

  .vs__selected-options {
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    position: relative !important;
    overflow: hidden !important;
  }

  .vs__actions {
    display: flex !important;
    align-items: center !important;
    flex-shrink: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    gap: 4px !important;
  }

  .vs__clear {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    background: transparent !important;
    cursor: pointer !important;
    fill: #6E6B7B !important;
    line-height: 1 !important;

    svg {
      transform: scale(0.85);
      vertical-align: middle;
    }
  }

  .vs__open-indicator {
    display: inline-flex !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
    transform: scale(0.85);
  }

  .vs__placeholder {
    color: #0A0A0A !important;
    margin: 0 !important;
    padding: 0 !important;
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    line-height: normal !important;
    opacity: 1 !important;
    pointer-events: none !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 100% !important;
  }

  .vs__selected {
    color: #0A0A0A !important;
    margin: 0 !important;
    padding: 0 !important;
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    line-height: normal !important;
    pointer-events: none !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 100% !important;
  }

  &.vs--single.vs--open {
    .vs__selected,
    .vs__placeholder {
      margin-top: 0 !important;
      transform: translateY(-50%) !important;
    }
  }

  .vs__search,
  .vs__search:focus {
    margin: 0 !important;
    padding: 0 !important;
    line-height: normal !important;
    border: none !important;
    box-shadow: none !important;
    height: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    background: transparent !important;
    position: relative !important;
    z-index: 1 !important;
  }

  &.vs--searching {
    .vs__placeholder,
    .vs__selected {
      display: none !important;
    }
  }

  .vs__dropdown-menu {
    border-radius: 8px !important;
    border: 1px solid #E5E5E5 !important;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
    min-width: 100% !important;
    width: max-content !important;
    max-width: min(400px, 90vw) !important;
    overflow-x: hidden !important;
  }

  .vs__dropdown-option {
    padding: 8px 12px !important;
    white-space: normal !important;
    word-break: break-word !important;
    line-height: 1.4 !important;
  }

  .vs__dropdown-option--highlight {
    background-color: #F5F5F5 !important;
    color: #0A0A0A !important;
  }

  // Non-blocking backend-search indicator rendered below the existing list items.
  .search-loading-li {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    height: 36px;
    font-size: 13px;
    color: #A3A3A3;
    border-top: 1px solid #F5F5F5;
  }
}
</style>
